import { useMutation } from "@apollo/client";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import React, { FC, KeyboardEvent, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { SIGNUP_PROGRESSION } from "../../graphql/account/mutation";
import {
  SignupProgression,
  SignupProgressionVariables,
} from "../../graphql/account/types/SignupProgression";
import { ERROR_MESSAGE, SAPHEER_COLOR } from "../../utils/constant";
import useStyles from "./style";
import { signupProgression } from "../../enums/account";
import { CreateUserInput } from "../../types/graphql-global-types-onboarding";
import { CREATE_PAYMENT_STRIPE } from "../../graphql/onboarding/stripe/mutation";
import {
  CreatePaymentStripe,
  CreatePaymentStripeVariables,
} from "../../graphql/onboarding/stripe/types/CreatePaymentStripe";
import LeaderModal from "./LeaderModal/LeaderModal";
import { SelectItemInterface } from "../../interface/SelectItemInterface";
import StatusGetID from "../GetId/statusGetId";
import Person from "../Common/Person/Person";
import Address from "../Common/Address/Address";
import CustomCheckBox from "../Ui/CustomCheckBox";
import { CustomButton } from "../Ui/CustomButton";
import { useToastNotifications } from "../../utils/toastUtils";
import { CREATE_USER } from "../../graphql/onboarding/user/mutation";
import {
  CreateUser,
  CreateUserVariables,
} from "../../graphql/onboarding/user/types/CreateUser";
import DocumentUser from "../Common/DocumentUser/DocumentUser";
import { DocumentsType_documentsType } from "../../graphql/onboarding/document/types/DocumentsType";

interface LeaderProps {
  owner: CreateUserInput;
  previous: string;
  next: string;
  isIndividual: boolean;
  personItems: Array<SelectItemInterface>;
  representativesList: Array<CreateUserInput>;
  documentsType: (DocumentsType_documentsType | null)[];
}

const Leader: FC<LeaderProps> = (props) => {
  const {
    previous,
    next,
    owner,
    isIndividual,
    personItems,
    representativesList,
    documentsType,
  } = props;
  const classes = useStyles();
  const navigate = useNavigate();

  const { showErrorToast } = useToastNotifications();
  const [information, setInformation] = useState<CreateUserInput>(owner);
  const [open, setOpen] = useState(personItems?.length > 1);
  const [accept, setAccept] = useState({
    accept: !!owner.id,
    agree: !!owner.id,
  });

  const { active, loading } = StatusGetID();
  const [finishStep, { loading: finishStepLoading }] = useMutation<
    SignupProgression,
    SignupProgressionVariables
  >(SIGNUP_PROGRESSION, {
    onCompleted: () => {
      if (isIndividual) {
        const cancelUrl = window.location.href;
        const successUrl = cancelUrl.replace(
          "manager",
          active ? "identity-verification" : "contrat",
        );
        createPayment({
          variables: {
            cancelUrl,
            successUrl,
          },
        });
      } else {
        navigate(next);
      }
    },
    onError: (error) => {
      showErrorToast({ description: "server.error" });
    },
  });

  const [createPayment, { loading: stripeLoading }] = useMutation<
    CreatePaymentStripe,
    CreatePaymentStripeVariables
  >(CREATE_PAYMENT_STRIPE, {
    onCompleted: (url) => {
      if (url.createPaymentStripe) {
        navigate("/manager", { replace: true });
        window.location.replace(url.createPaymentStripe);
      }
    },
    onError: (error) => {
      showErrorToast({ description: "create.payment.stripe.error" });
    },
  });

  // Création du propriétaire
  const [createOwner, { loading: CreateLoading }] = useMutation<
    CreateUser,
    CreateUserVariables
  >(CREATE_USER, {
    onCompleted: () => {
      if (isIndividual) {
        const cancelUrl = window.location.href;
        const successUrl = cancelUrl.replace(
          "manager",
          active ? "identity-verification" : "contrat",
        );
        createPayment({
          variables: {
            cancelUrl,
            successUrl,
          },
        });
      } else {
        navigate(next);
      }
    },
    onError: (error) => {
      const { graphQLErrors, networkError } = error;
      let message = "";
      if (graphQLErrors) message = "server.error";
      if (
        graphQLErrors &&
        graphQLErrors[0] &&
        graphQLErrors[0].message === ERROR_MESSAGE.HOSTED_DOCS_LENGTH_ERROR
      ) {
        message = "hosted.enough.document";
      } else if (networkError) message = "network.error";
      else message = "other.error";
      showErrorToast({ description: message });
    },
  });

  // Gestion des changements sur les inputs checkbox
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setAccept({ ...accept, [name]: checked });
  };

  // Gestion des changements sur les inputs classiques
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value: valueEvent, checked } = event.target;
    if (name === "specifiedUSPerson" || name === "isDirector") {
      setInformation({ ...information, [name]: checked });
    } else {
      let value: any;
      if (valueEvent === "true" || valueEvent === "false") {
        value = valueEvent === "true";
      } else {
        value = valueEvent;
      }
      setInformation({ ...information, [name]: value });
    }
  };

  const handleSelect = (name: string, value: string) => {
    setInformation({ ...information, [name]: value });
  };

  const handleDateChange = (name: string, date: any) => {
    setInformation({ ...information, [name]: date });
  };

  // Remplacer history.replace par navigate avec l'option { replace: true }
  const handlePrevious = () => {
    navigate(previous, { replace: true });
  };

  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const handleChangeLeader = (id: string) => {
    const result = representativesList.find(
      (p) =>
        `${p?.firstname || ""} ${p?.name || ""} ${
          p?.nationalityCountryIsoCode || ""
        } ${p?.city || ""} ${p?.address || ""} ${p?.countryIsoCode || ""} ${
          p?.postcode || ""
        }` === id,
    );
    if (result) {
      setInformation(result);
    }
    handleClose();
  };

  const handleNext = async () => {
    setIsSubmitted(true);
    if (accept?.accept && accept?.agree) {
      const {
        city,
        address,
        cityOfBirth,
        countryIsoCode,
        countryOfBirthIsoCode,
        dateOfBirth,
        firstname,
        gender,
        name,
        nationalityCountryIsoCode,
        postcode,
        documents,
        specifiedUSPerson,
        isDirector,
        directorAppointmentDate,
      } = information;
      // Validation des inputs
      if (!gender) {
        return showErrorToast({ description: "member.gender.empty" });
      }
      if (!name) {
        return showErrorToast({ description: "member.name.empty" });
      }
      if (!firstname) {
        return showErrorToast({ description: "member.firstname.empty" });
      }
      if (!nationalityCountryIsoCode) {
        return showErrorToast({ description: "member.nationality.empty" });
      }
      if (!specifiedUSPerson) {
        return showErrorToast({ description: "member.usPerson.empty" });
      }
      if (!address) {
        return showErrorToast({ description: "address.street.empty" });
      }
      if (!countryIsoCode) {
        return showErrorToast({ description: "address.isoCountryCode.empty" });
      }
      if (!postcode) {
        return showErrorToast({ description: "address.postalCode.empty" });
      }
      if (!city) {
        return showErrorToast({ description: "address.city.empty" });
      }
      if (isDirector && !directorAppointmentDate) {
        return showErrorToast({
          description: "member.director.appointment.date.empty",
        });
      }

      createOwner({
        variables: {
          input: {
            gender,
            city,
            firstname,
            name,
            isLegalRepresentative: true,
            specifiedUSPerson,
            address,
            cityOfBirth,
            countryIsoCode,
            countryOfBirthIsoCode,
            dateOfBirth,
            nationalityCountryIsoCode,
            postcode,
            documents,
            type: "LEADER",
            directorAppointmentDate,
            isDirector,
          },
        },
      });
    } else {
      showErrorToast({ description: "stepFive.createPerson.accept" });
    }
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleNext();
    }
  };

  return (
    <>
      <form onKeyPress={handleKeyPress}>
        <Box className={classes.box}>
          <Box className={classes.boxRow}>
            <Person
              information={information}
              handleInputChange={handleInputChange}
              handleSelect={handleSelect}
              handleDateChange={handleDateChange}
              isSubmitted={isSubmitted}
            />
          </Box>
          <Box className={classes.boxRow}>
            <Address
              information={information}
              handleInputChange={handleInputChange}
              isSubmitted={isSubmitted}
              handleSelect={handleSelect}
            />
            <DocumentUser
              information={information}
              setInformation={setInformation}
              documentsType={documentsType ?? []}
            />

            <CustomCheckBox
              allPointer
              name="agree"
              isSubmitted={isSubmitted}
              isChecked={accept.agree}
              onCheck={handleChange}
              label={
                <Typography
                  className={classes.text}
                  style={{ fontWeight: "bold", color: SAPHEER_COLOR }}
                >
                  <FormattedMessage id="member.address.agree" />
                </Typography>
              }
              errorMessage={<FormattedMessage id="member.form.error.agree" />}
            />
            <CustomCheckBox
              allPointer
              name="accept"
              isSubmitted={isSubmitted}
              isChecked={accept.accept}
              onCheck={handleChange}
              label={
                <Typography
                  className={classes.text}
                  style={{ fontWeight: "bold", color: SAPHEER_COLOR }}
                >
                  <FormattedMessage id="member.certify.information" />
                </Typography>
              }
              errorMessage={<FormattedMessage id="member.error.accept" />}
            />
          </Box>
        </Box>
      </form>
      <Box className={classes.button}>
        <CustomButton
          type="button"
          variant="outline4"
          onClick={handlePrevious}
          label="button.previous"
          disabled={loading}
          fullWidthMobile
        />
        <CustomButton
          type="button"
          variant="primary"
          onClick={handleNext}
          label="button.next"
          loading={
            CreateLoading || finishStepLoading || loading || stripeLoading
          }
          fullWidthMobile
        />
      </Box>
      <LeaderModal
        isLeader={personItems?.length > 1 && open}
        personItems={personItems}
        handleClose={handleClose}
        handleChangeLeader={handleChangeLeader}
      />
    </>
  );
};

export default Leader;
